/**
 * Çalışma takvimi (M3 K-M3-02; SLA tüketicisi WP 4.3 destek talepleri). Saf hesap: dış bağımlılık yok, saat dilimi dönüşümü
 * `Intl` ile yapılır (yaz saati geçişleri dahil).
 *
 * Kurallar (sahip kararı 2026-09-27):
 * - Varsayılan Europe/Istanbul, Pazartesi–Cuma 09:00–18:00; saat dilimi, günler, saatler ve kapalı günler şirket ayarıdır.
 * - Resmî tatiller otomatik DEĞİLDİR: kapalı gün elle girilir.
 * - Takvim dışında başlayan süre ilk çalışma aralığında saymaya başlar.
 * - Duraklatma yalnız AÇIK durum kuralıyla: `pausingStatuses` boşsa hiçbir durum süreyi durdurmaz.
 * - Takvim sürümlüdür: bir süre, her anında yürürlükte olan sürümle hesaplanır; yeni sürüm geçmişi yeniden yazmaz.
 * - Bu hesap operasyonel izlemedir; hukuki taahhüt değildir.
 */
import { z } from 'zod';
import { isValidTimeZone } from './record-extras.js';
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;
/** Bir hesabın tarayabileceği en çok gün (≈ 10 yıl): çalışma zamanı olmayan takvimde sonsuz döngüyü önler. */
export const WORK_CALENDAR_MAX_SCAN_DAYS = 3700;
export const ISO_WEEKDAYS = [1, 2, 3, 4, 5, 6, 7];
export const WEEKDAY_LABELS = {
    1: 'Pazartesi',
    2: 'Salı',
    3: 'Çarşamba',
    4: 'Perşembe',
    5: 'Cuma',
    6: 'Cumartesi',
    7: 'Pazar',
};
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'saat SS:DD olmalı');
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
export function minutesOf(t) {
    const [h, m] = t.split(':');
    return Number(h) * 60 + Number(m);
}
export const workIntervalSchema = z
    .object({
    weekday: z.union([
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5),
        z.literal(6),
        z.literal(7),
    ]),
    start: hhmm,
    end: hhmm,
})
    .refine((i) => minutesOf(i.start) < minutesOf(i.end), {
    message: 'başlangıç bitişten önce olmalı',
    path: ['end'],
});
export const closedDateSchema = z.object({
    date: isoDate,
    label: z.string().trim().max(80).optional(),
});
export const workCalendarSchema = z
    .object({
    timeZone: z.string().min(3).max(64).refine(isValidTimeZone, 'geçersiz saat dilimi'),
    intervals: z.array(workIntervalSchema).max(28),
    closedDates: z.array(closedDateSchema).max(500),
})
    .superRefine((c, ctx) => {
    for (const d of ISO_WEEKDAYS) {
        const day = c.intervals
            .filter((i) => i.weekday === d)
            .map((i) => [minutesOf(i.start), minutesOf(i.end)])
            .sort((a, b) => a[0] - b[0]);
        for (let k = 1; k < day.length; k++)
            if ((day[k]?.[0] ?? 0) < (day[k - 1]?.[1] ?? 0))
                ctx.addIssue({
                    code: 'custom',
                    message: `${WEEKDAY_LABELS[d]} aralıkları çakışıyor`,
                    path: ['intervals'],
                });
    }
    const seen = new Set();
    for (const cd of c.closedDates) {
        if (seen.has(cd.date))
            ctx.addIssue({
                code: 'custom',
                message: `kapalı gün tekrarlı: ${cd.date}`,
                path: ['closedDates'],
            });
        seen.add(cd.date);
    }
});
export const DEFAULT_WORK_CALENDAR = {
    timeZone: 'Europe/Istanbul',
    intervals: [1, 2, 3, 4, 5].map((weekday) => ({
        weekday,
        start: '09:00',
        end: '18:00',
    })),
    closedDates: [],
};
// ---------------------------------------------------------------------------------------------------------------------
// Saat dilimi yardımcıları
// ---------------------------------------------------------------------------------------------------------------------
const fmtCache = new Map();
function fmt(tz) {
    let f = fmtCache.get(tz);
    if (!f) {
        f = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            hourCycle: 'h23',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
        fmtCache.set(tz, f);
    }
    return f;
}
function wallOf(tz, utcMs) {
    const p = {};
    for (const part of fmt(tz).formatToParts(new Date(utcMs)))
        if (part.type !== 'literal')
            p[part.type] = Number(part.value);
    return {
        y: p['year'] ?? 0,
        m: p['month'] ?? 0,
        d: p['day'] ?? 0,
        hh: p['hour'] ?? 0,
        mm: p['minute'] ?? 0,
        ss: p['second'] ?? 0,
    };
}
/** Saat dilimi farkı (yerel − UTC), ms. */
function offsetMs(tz, utcMs) {
    const w = wallOf(tz, utcMs);
    const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.hh, w.mm, w.ss);
    return asUtc - Math.floor(utcMs / 1000) * 1000;
}
/** Yerel duvar saati → UTC anı (yaz saati geçişinde ileri kayan saat bir sonraki geçerli ana düşer). */
export function localToUtc(tz, y, m, d, minutes) {
    const guess = Date.UTC(y, m - 1, d, 0, minutes);
    const first = guess - offsetMs(tz, guess);
    return guess - offsetMs(tz, first);
}
function localDate(tz, utcMs) {
    const w = wallOf(tz, utcMs);
    return { y: w.y, m: w.m, d: w.d };
}
function shiftDate(y, m, d, days) {
    const t = new Date(Date.UTC(y, m - 1, d) + days * DAY_MS);
    return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}
function isoDateStr(y, m, d) {
    return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function isoWeekday(y, m, d) {
    const w = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return (w === 0 ? 7 : w);
}
/** Bir yerel günün çalışma pencereleri (UTC anları, sıralı). Kapalı günde boş. */
export function dayWindows(cal, date) {
    const iso = isoDateStr(date.y, date.m, date.d);
    if (cal.closedDates.some((c) => c.date === iso))
        return [];
    const wd = isoWeekday(date.y, date.m, date.d);
    return cal.intervals
        .filter((i) => i.weekday === wd)
        .map((i) => [
        localToUtc(cal.timeZone, date.y, date.m, date.d, minutesOf(i.start)),
        localToUtc(cal.timeZone, date.y, date.m, date.d, minutesOf(i.end)),
    ])
        .filter(([s, e]) => e > s)
        .sort((a, b) => a[0] - b[0]);
}
// ---------------------------------------------------------------------------------------------------------------------
// Hesaplar
// ---------------------------------------------------------------------------------------------------------------------
/** [from, to) aralığındaki çalışma süresi (ms). */
export function workingMs(from, to, cal) {
    if (!(to > from))
        return 0;
    const f = localDate(cal.timeZone, from);
    const start = shiftDate(f.y, f.m, f.d, -1);
    const endDate = localDate(cal.timeZone, to);
    const endKey = Date.UTC(endDate.y, endDate.m - 1, endDate.d) + DAY_MS;
    let total = 0;
    for (let i = 0; i <= WORK_CALENDAR_MAX_SCAN_DAYS; i++) {
        const day = shiftDate(start.y, start.m, start.d, i);
        if (Date.UTC(day.y, day.m - 1, day.d) > endKey)
            return total;
        for (const [s, e] of dayWindows(cal, day)) {
            const a = Math.max(s, from);
            const b = Math.min(e, to);
            if (b > a)
                total += b - a;
        }
    }
    throw new RangeError('çalışma takvimi hesabı tarama sınırını aştı');
}
/** `from` anından itibaren `amountMs` çalışma süresi dolduğunda ulaşılan an (takvim dışı başlangıç ilk aralıkta sayar). */
export function addWorkingMs(from, amountMs, cal) {
    if (amountMs < 0)
        throw new RangeError('süre negatif olamaz');
    const start = localDate(cal.timeZone, from);
    let remaining = amountMs;
    for (let i = -1; i <= WORK_CALENDAR_MAX_SCAN_DAYS; i++) {
        for (const [s, e] of dayWindows(cal, shiftDate(start.y, start.m, start.d, i))) {
            if (e <= from)
                continue;
            const a = Math.max(s, from);
            if (remaining <= e - a)
                return a + remaining;
            remaining -= e - a;
        }
    }
    throw new RangeError('takvimde yeterli çalışma zamanı yok');
}
/** `from` anında veya sonrasındaki ilk çalışma anı. */
export function nextWorkingInstant(from, cal) {
    return addWorkingMs(from, 0, cal);
}
/**
 * `at` anı, O ANDA yürürlükteki takvim sürümüne göre çalışma saatleri içinde mi (K-M7-50.5: SLA "şu an mesai dışı" alanı).
 * Pencereler yerel duvar saatinden (takvimin IANA saat dilimi) üretilir; yaz saati geçişleri saat dilimi veritabanıyla çözülür.
 * İlk sürümden önceki an ilk sürümle değerlendirilir (sürüm yoksa varsayılan takvim) — `workingMsVersioned` ile aynı kural.
 */
export function isWorkingInstant(at, versions) {
    const sorted = [...versions].sort((a, b) => a.effectiveFrom - b.effectiveFrom);
    let cal = sorted[0]?.calendar ?? DEFAULT_WORK_CALENDAR;
    for (const v of sorted)
        if (v.effectiveFrom <= at)
            cal = v.calendar;
    return dayWindows(cal, localDate(cal.timeZone, at)).some(([s, e]) => at >= s && at < e);
}
function segments(from, to, versions) {
    const sorted = [...versions].sort((a, b) => a.effectiveFrom - b.effectiveFrom);
    if (sorted.length === 0)
        return [{ from, to, calendar: DEFAULT_WORK_CALENDAR }];
    const out = [];
    sorted.forEach((v, i) => {
        // İlk sürümden önceki zaman ilk sürümle hesaplanır (daha eski bilgi yoktur)
        const vFrom = i === 0 ? -Infinity : v.effectiveFrom;
        const vTo = sorted[i + 1]?.effectiveFrom ?? Infinity;
        const a = Math.max(from, vFrom);
        const b = Math.min(to, vTo);
        if (b > a)
            out.push({ from: a, to: b, calendar: v.calendar });
    });
    return out;
}
/** Sürümlü takvimle çalışma süresi: her an kendi yürürlükteki sürümüyle sayılır (geçmiş yeniden yazılmaz). */
export function workingMsVersioned(from, to, versions) {
    return segments(from, to, versions).reduce((sum, s) => sum + workingMs(s.from, s.to, s.calendar), 0);
}
/**
 * Sürümlü takvimle ileri hesap (M4 WP 4.3 SLA hedef anı): `from` anından itibaren `amountMs` çalışma süresi dolduğunda ulaşılan
 * an; her aralık kendi yürürlükteki sürümüyle sayılır (`workingMsVersioned` ile tutarlı). Son sürüm sonsuza kadar geçerlidir.
 */
export function addWorkingMsVersioned(from, amountMs, versions) {
    if (amountMs < 0)
        throw new RangeError('süre negatif olamaz');
    let remaining = amountMs;
    for (const s of segments(from, Infinity, versions)) {
        if (s.to === Infinity)
            return addWorkingMs(s.from, remaining, s.calendar);
        const available = workingMs(s.from, s.to, s.calendar);
        if (available > 0 && remaining <= available)
            return addWorkingMs(s.from, remaining, s.calendar);
        remaining -= available;
    }
    throw new RangeError('takvimde yeterli çalışma zamanı yok');
}
/**
 * Durum tarihçesinden duraklama aralıkları: yalnız `pausingStatuses` içindeki durumlarda geçen süre. Boş küme = duraklama yok
 * (hiçbir durum örtük olarak süreyi durdurmaz). Tarihçe zamana göre sıralanır; son durum `until` anına kadar sürer.
 */
export function pauseIntervals(history, pausingStatuses, until) {
    if (pausingStatuses.size === 0)
        return [];
    const h = [...history].sort((a, b) => a.at - b.at);
    const out = [];
    h.forEach((e, i) => {
        if (!pausingStatuses.has(e.status))
            return;
        const end = Math.min(h[i + 1]?.at ?? until, until);
        if (end > e.at)
            out.push([e.at, end]);
    });
    return out;
}
/** Duraklamalar düşülmüş sürümlü çalışma süresi (duraklama aralıkları birleştirilip kırpılır). */
export function workingMsExcludingPauses(from, to, versions, pauses) {
    const merged = [];
    for (const [a0, b0] of [...pauses].sort((x, y) => x[0] - y[0])) {
        const a = Math.max(a0, from);
        const b = Math.min(b0, to);
        if (b <= a)
            continue;
        const last = merged[merged.length - 1];
        if (last && a <= last[1])
            last[1] = Math.max(last[1], b);
        else
            merged.push([a, b]);
    }
    const paused = merged.reduce((s, [a, b]) => s + workingMsVersioned(a, b, versions), 0);
    return workingMsVersioned(from, to, versions) - paused;
}
export const workingHours = (ms) => ms / HOUR_MS;
// ---------------------------------------------------------------------------------------------------------------------
// API sözleşmeleri
// ---------------------------------------------------------------------------------------------------------------------
export const updateWorkCalendarInputSchema = z
    .object({ expectedVersion: z.number().int().min(0), calendar: workCalendarSchema })
    .strict();
export const workCalendarViewSchema = z.object({
    /** 0 = hiç kaydedilmedi (varsayılan takvim) */
    version: z.number().int(),
    effectiveFrom: z.string().nullable(),
    calendar: workCalendarSchema,
    history: z.array(z.object({
        version: z.number().int(),
        effectiveFrom: z.string(),
        createdByName: z.string().nullable(),
    })),
});
//# sourceMappingURL=work-calendar.js.map